import { logger } from "../tslog";

const slideImageCache = new Map<string, { data: string; timestamp: number }>();
const CACHE_DURATION = 5000; // 5 seconds cache duration

async function getSlideImageData(
  context: PowerPoint.RequestContext,
  slide: any
): Promise<string | null> {
  const slideId = slide.id;
  const now = Date.now();
  
  // Check cache first
  const cached = slideImageCache.get(slideId);
  if (cached && (now - cached.timestamp) < CACHE_DURATION) {
    logger.info("[Slide] Using cached image data", { slideId });
    return cached.data;
  }
  
  try {
    // Validate slide object
    if (!slide || !slide.id) {
      logger.warn("[Slide] Invalid slide object provided to getSlideImageData");
      return null;
    }
    
    // Ensure slide is loaded
    slide.load(["id"]);
    await context.sync();
    
    // Add small delay to ensure slide is ready
    await new Promise(resolve => setTimeout(resolve, 50));
    
    const slideImg = slide.getImageAsBase64();
    await context.sync();
    
    if (!slideImg || !slideImg.value) {
      logger.warn("[Slide] No image data returned from getImageAsBase64", { slideId });
      return null;
    }
    
    // Cache the result
    slideImageCache.set(slideId, { data: slideImg.value, timestamp: now });
    logger.info("[Slide] Cached new image data", { slideId });
    
    return slideImg.value;
  } catch (e) {
    logger.warn("[Slide] Failed to get slide image data", { 
      error: (e as any)?.message || String(e),
      slideId: slide?.id || 'unknown'
    });
    return null;
  }
}

export async function getSlideSize(
    context: PowerPoint.RequestContext,
    slide: any
  ): Promise<{ width: number; height: number }> {
    try {
      // Get slide image data using cached approach
      const imageData = await getSlideImageData(context, slide);
      
      if (!imageData) {
        logger.warn("[Slide] No image data available for getSlideSize", { slideId: slide?.id });
        return { width: 0, height: 0 };
      }
      
      const img = new Image();
      img.src = "data:image/png;base64, " + imageData;
      await img.decode();
      
      // Convert pixels to points: first convert pixels to inches (96 DPI), then multiply by 72 points per inch
      // Step 1: Convert pixels to inches (96 pixels = 1 inch)
      const heightInches = img.height / 96;
      const widthInches = img.width / 96;
      
      // Step 2: Convert inches to points (72 points = 1 inch)
      const height = heightInches * 72;
      const width = widthInches * 72;
      
      try {
        logger.info("[Slide] size from image", { 
          widthPixels: img.width, 
          heightPixels: img.height, 
          heightInches,
          widthInches,
          widthPoints: width,
          heightPoints: height,
          slideId: slide.id,
         
        });
      } catch {}
      
      return { width, height };
    } catch (e) {
      try {
        logger.warn("[Slide] Failed to get slide size from image", { 
          error: (e as any)?.message || String(e),
          slideId: slide?.id || 'unknown'
        });
      } catch {}
    }
    return { width: 0, height: 0 };
  }

export async function warmUpSlide(
  context: PowerPoint.RequestContext,
  slide: any
): Promise<void> {
  try {
    // Validate slide state
    if (!slide || !slide.id) {
      logger.warn("[Slide] Invalid slide object provided to warmUpSlide");
      return;
    }
    
    // Use cached approach to get slide image data
    const imageData = await getSlideImageData(context, slide);
    
    if (imageData) {
      logger.info("[Slide] Warm-up completed successfully using cached data", { 
        slideId: slide.id 
      });
    } else {
      logger.warn("[Slide] Warm-up failed - no image data available", { 
        slideId: slide.id 
      });
    }
  } catch (e) {
    logger.warn("[Slide] Warm-up getImageAsBase64 failed", { 
      error: (e as any)?.message || String(e),
      slideId: slide?.id || 'unknown'
    });
  }
}


export function clearSlideImageCache(): void {
  slideImageCache.clear();
  logger.info("[Slide] Image cache cleared");
}

export function clearSlideImageCacheForSlide(slideId: string): void {
  slideImageCache.delete(slideId);
  logger.info("[Slide] Image cache cleared for slide", { slideId });
}